import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import Home from './Home';
import OrderHistory from './OrderHistory';
import { clientsService } from '../services/api';
import { useFormatage } from '../hooks/useAgroshopHooks';
import { getErrorMessage } from '../utils/errorFormatter';

const SUPPORT_PHONE = '+22374146816';
const SUPPORT_WHATSAPP = '22374146816';
const SUPPORT_EMAIL = 'it43694@gmail.com';

const normaliserTelephone = (telephone) => {
  return String(telephone || '').trim().replace(/\s+/g, '').replace(/[^+0-9]/g, '');
};

const ouvrirAppel = (telephone) => {
  const numero = normaliserTelephone(telephone);
  if (!numero) return;
  window.location.href = `tel:${numero}`;
};

const ouvrirWhatsApp = (telephone) => {
  const numero = String(telephone || '').trim().replace(/\s+/g, '').replace(/[^0-9]/g, '');
  if (!numero) return;
  window.open(`https://wa.me/${numero}`, '_blank', 'noopener,noreferrer');
};

const ouvrirEmail = (email) => {
  const dest = String(email || '').trim();
  if (!dest) return;
  const sujet = encodeURIComponent('Demande de conseil AgroShop');
  const body = encodeURIComponent('Bonjour AgroShop,\n\nJe souhaite recevoir des conseils sur mon approvisionnement et mes semis.\n\nMerci.');
  window.location.href = `mailto:${dest}?subject=${sujet}&body=${body}`;
};

function ContactOptionsModal({ onClose }) {
  return (
    <div className="ag-modal-overlay" style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.45)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px',
    }}>
      <div className="ag-modal-card" style={{
        width: '100%',
        maxWidth: '420px',
        backgroundColor: '#fff',
        borderRadius: '14px',
        boxShadow: '0 32px 80px rgba(0,0,0,0.2)',
        padding: '24px',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h3 style={{ margin: 0 }}>Contactez-nous</h3>
            <p style={{ margin: '8px 0 0', color: '#555' }}>Choisissez un canal pour obtenir des conseils rapidement.</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'transparent', border: 'none', fontSize: '20px', cursor: 'pointer' }}
            aria-label="Fermer"
          >
            ×
          </button>
        </div>
        <div style={{ display: 'grid', gap: '12px' }}>
          <button
            type="button"
            className="ag-btn ag-btn--primary"
            onClick={() => {
              ouvrirWhatsApp(SUPPORT_WHATSAPP);
              onClose();
            }}
          >
            Ouvrir WhatsApp
          </button>
          <button
            type="button"
            className="ag-btn ag-btn--secondary"
            onClick={() => {
              ouvrirAppel(SUPPORT_PHONE);
              onClose();
            }}
          >
            Appeler {SUPPORT_PHONE}
          </button>
          <button
            type="button"
            className="ag-btn"
            onClick={() => {
              ouvrirEmail(SUPPORT_EMAIL);
              onClose();
            }}
          >
            Envoyer un e-mail
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard({ onAdd, currentUser, espaceMode = false }) {
  const navigate = useNavigate();
  const [resume, setResume] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [showContactOptions, setShowContactOptions] = useState(false);
  const { formatPrix } = useFormatage();

  const calendrierRegion = currentUser?.client?.region || 'votre zone';
  const culturesPratiques = currentUser?.client?.cultures_pratiquees || [];
  const cultureText = culturesPratiques.length > 0 ? culturesPratiques.join(', ') : 'vos cultures clés';
  const moisActuel = new Date().toLocaleString('fr-FR', { month: 'long' });
  const saisonActuelle = new Date().getMonth() >= 5 && new Date().getMonth() <= 9 ? 'saison des pluies' : 'saison sèche';
  const calendrierClient = [
    { titre: 'Prochain jalon', texte: `Planifiez votre approvisionnement en semences pour ${cultureText} sur ${saisonActuelle}.` },
    { titre: `Au ${moisActuel}`, texte: `Vérifiez les intrants et la disponibilité locale pour ${calendrierRegion}.` },
    { titre: 'Conseil', texte: 'Préparez une commande de protection et encouragez les bons rendements en suivant votre plan de rotation.' },
  ];

  useEffect(() => {
    if (!espaceMode) return undefined;

    let mounted = true;

    const chargerResume = async () => {
      if (!currentUser?.client?.id) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setErreur(null);

      try {
        const [resultat, recommandations] = await Promise.all([
          clientsService.getResume(currentUser.client.id),
          clientsService.getRecommendations(currentUser.client.id).catch(() => []),
        ]);
        if (mounted) {
          setResume(resultat);
          setRecommendations(Array.isArray(recommandations) ? recommandations : recommandations.results || []);
        }
      } catch (e) {
        if (mounted) setErreur(getErrorMessage(e));
      } finally {
        if (mounted) setLoading(false);
      }
    };

    chargerResume();
    return () => { mounted = false; };
  }, [currentUser?.client?.id, espaceMode]);

  const stats = resume?.stats || {};
  const clientNom = resume?.client?.nom_complet || 'cher client';
  const pointsFidelite = stats.points_fidelite || 0;
  const nbCommandes = stats.nb_commandes || 0;
  const totalDepense = stats.total_achats || 0;
  const panierMoyen = stats.panier_moyen || 0;

  const location = useLocation();
  const tabs = [
    { label: 'Catalogue', path: '/client' },
    { label: 'Mon espace', path: '/client/espace' },
  ];

  return (
    <div className="ag-dashboard-page">
      <section className="ag-dashboard-intro">
        <div className="ag-dashboard-hero">
          <div>
            <p className="ag-hero-label">Espace client</p>
            <h2>Bonjour {clientNom}</h2>
            <p>Bienvenue dans votre espace client. Suivez vos points, vos commandes et obtenez des conseils personnalisés.</p>
          </div>
          <div className="ag-dashboard-actions">
            <button
              type="button"
              className="ag-btn ag-btn--secondary"
              onClick={() => setShowContactOptions(true)}
            >
              Contacter le service client
            </button>
            <button
              type="button"
              className="ag-btn"
              onClick={() => navigate(espaceMode ? '/client' : '/client/espace')}
            >
              {espaceMode ? 'Retour accueil' : 'Voir mon espace'}
            </button>
          </div>
        </div>
        <div className="ag-dashboard-tabs" role="tablist">
          {tabs.map((tab) => (
            <button
              key={tab.path}
              type="button"
              className={`ag-tab ${location.pathname === tab.path ? 'ag-tab--active' : ''}`}
              onClick={() => navigate(tab.path)}
              aria-pressed={location.pathname === tab.path}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </section>

      <section className="ag-dashboard-status">
        <div className="ag-status-card">Analyse terminée ✅ Votre espace client est à jour.</div>
      </section>

      {espaceMode && (
        <>
          <section className="ag-dashboard-grid" style={{ marginBottom: '24px' }}>
          <div className="ag-dashboard-card">
          <div className="ag-card-label">Points fidélité</div>
          <strong className="ag-card-value">{pointsFidelite.toLocaleString()}</strong>
          <div className="ag-card-sub">≈ {formatPrix(pointsFidelite * 10)} de réduction</div>
        </div>
        <div className="ag-dashboard-card">
          <div className="ag-card-label">Mes commandes</div>
          <strong className="ag-card-value">{nbCommandes}</strong>
          <div className="ag-card-sub">Commandes enregistrées</div>
        </div>
        <div className="ag-dashboard-card">
          <div className="ag-card-label">Total dépensé</div>
          <strong className="ag-card-value">{formatPrix(totalDepense)}</strong>
          <div className="ag-card-sub">Depuis votre inscription</div>
        </div>
        <div className="ag-dashboard-card">
          <div className="ag-card-label">Panier moyen</div>
          <strong className="ag-card-value">{formatPrix(panierMoyen)}</strong>
          <div className="ag-card-sub">Sur vos commandes les plus récentes</div>
        </div>
      </section>

      <section className="ag-dashboard-grid" style={{ gap: '18px', marginBottom: '24px' }}>
        <div className="ag-dashboard-card">
          <h3>Garantie & retours</h3>
          <ul className="ag-list" style={{ marginTop: '12px' }}>
            <li>Retour possible sous 7 jours pour produit non conforme.</li>
            <li>Garantie satisfaction sur toutes les commandes locales.</li>
            <li>Support dédié pour les échanges et ajustements de livraison.</li>
            <li>Protection des données personnelles conforme à notre <Link to="/privacy">politique de confidentialité</Link>.</li>
          </ul>
        </div>
        <div className="ag-dashboard-card">
          <h3>Conseil pratique</h3>
          <div className="ag-list" style={{ marginTop: '12px' }}>
            {calendrierClient.map((item) => (
              <div key={item.titre} className="ag-summary-item" style={{ display: 'block', marginBottom: '12px' }}>
                <strong>{item.titre}</strong>
                <div>{item.texte}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
        </>
      )}

      {espaceMode && (
        loading ? (
          <div className="ag-container">Chargement de votre espace client...</div>
        ) : erreur ? (
          <div className="ag-alert ag-alert-error">Erreur : {erreur}</div>
        ) : (
          <>
            <section className="ag-dashboard-grid" style={{ gap: '18px', marginBottom: '24px' }}>
              <div className="ag-dashboard-card" style={{ flex: 2 }}>
                <h3>Ma commande en cours</h3>
                <div className="ag-card-inline" style={{ justifyContent: 'space-between' }}>
                  <div>
                    <strong>CMD-2024-00201</strong>
                    <p className="ag-muted">3 sacs Mil Souna III + 2 sacs DAP · 178 000 FCFA</p>
                  </div>
                  <span className="ag-badge ag-badge--warning">En route</span>
                </div>
                <p className="ag-muted">Livraison prévue aujourd’hui — ETA 14h30</p>
              </div>

              <div className="ag-dashboard-card" style={{ flex: 1 }}>
                <h3>Mon calendrier cultural</h3>
                <ul className="ag-list-tight">
                  <li><strong>Dans 3 semaines</strong> — Semis mil · Commander DAP avant le 15 mai</li>
                  <li><strong>Juin</strong> — Herbicide pré-levée · Glyphosate disponible en stock</li>
                  <li><strong>Juillet</strong> — Urée topping · Précommande recommandée</li>
                </ul>
              </div>
            </section>

            <section className="ag-dashboard-grid" style={{ gap: '18px' }}>
              <div className="ag-dashboard-card" style={{ flex: 2 }}>
                <h3>Mes achats récents</h3>
                <OrderHistory />
              </div>

              <div className="ag-dashboard-card" style={{ flex: 1 }}>
                <h3>Recommandé pour moi</h3>
                <div className="ag-summary-data" style={{ flexDirection: 'column', gap: '16px' }}>
                  {recommendations.length > 0 ? (
                    recommendations.slice(0, 3).map((item) => (
                      <div key={item.id} className="ag-summary-item" style={{ width: '100%' }}>
                        <strong>{item.nom}</strong>
                        <span>{formatPrix(item.prix_unitaire)}</span>
                      </div>
                    ))
                  ) : (
                    <div className="ag-summary-item" style={{ width: '100%' }}>
                      <p className="ag-muted">Aucune recommandation personnalisée disponible pour l'instant.</p>
                    </div>
                  )}
                  <button
                    type="button"
                    className="ag-btn ag-btn--secondary"
                    onClick={() => setShowContactOptions(true)}
                  >
                    Demander un conseil
                  </button>
                </div>
              </div>
            </section>
          </>
        )
      )}

      {showContactOptions && (
        <ContactOptionsModal onClose={() => setShowContactOptions(false)} />
      )}

      <section className="ag-dashboard-section">
        <Home onAdd={onAdd} />
      </section>
    </div>
  );
}
